/**
 * Timezone Hook
 * Provides access to the current timezone with automatic sync to clinic context
 */

import { useEffect } from "react";
import { DEFAULT_TIMEZONE, getTimezoneWithFallback } from "@/config/timezone-config";
import { useClinicId, useOrganizationId } from "@/stores/domains/healthcare";
import {
  useCurrentTimezone as useCurrentTimezoneFromStore,
  useTimezoneStore,
} from "@/stores/domains/timezone";

/**
 * Get the current timezone with automatic sync to clinic context
 * Automatically updates timezone when clinic/organization changes
 * @returns Current timezone string (e.g., 'Asia/Singapore', 'Asia/Ho_Chi_Minh')
 */
export const useTimezone = (): string => {
  const clinicId = useClinicId();
  const organizationId = useOrganizationId();
  const timezone = useCurrentTimezoneFromStore();

  // Sync timezone with clinic context whenever clinic or organization changes
  useEffect(() => {
    const newTimezone = getTimezoneWithFallback(clinicId, organizationId);
    useTimezoneStore.getState().setTimezone(newTimezone);
  }, [clinicId, organizationId]);

  return timezone || DEFAULT_TIMEZONE;
};

/**
 * Alias for useTimezone for convenience
 */
export const useCurrentTimezone = useTimezone;

export default useTimezone;
